import { isPlatformBrowser, Location } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { finalConcatString, startConcatString } from '@shared/helper/configs';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  private readonly useragent!: string;
  isMac!: boolean;
  isIOS!: boolean;
  isSafari!: boolean;
  isMobile!: boolean;
  isSmartTv!: boolean;

  router = inject(Router);
  snackbar = inject(SnackbarService);
  location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    if (this.isBrowserOnly) {
      this.useragent = navigator.userAgent.toLowerCase();
      this.isIOS = !!this.useragent && /ipad|iphone|ipod/.test(this.useragent);
      this.isMac = !!this.useragent && /(mac|iphone|ipod|ipad)/i.test(this.useragent);
      this.isSafari =
        !!this.useragent && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      this.isMobile =
        !!this.useragent &&
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge|maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
          navigator.userAgent,
        );
    }
  }

  get isBrowserOnly(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  static mergeOneArrayInAnother(items: string | any[], allItems: any[]): any {
    for (let element of items) {
      allItems.push(element);
    }
    return allItems;
  }

  generateRandomString(len: number) {
    let text = '';
    const possible =
      'ABCDEF2634GHIJKLM23454g84OPQRg4gSThUVWXYZ4abcdef5345ghijklmn9op9qrstu69wxyz01hp2t34ed56f7b8N9';
    for (let i = 0; i < len; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  decodeObject(value: string) {
    let decodedString = value.substring(startConcatString, value.length - finalConcatString);
    try {
      return JSON.parse(atob(decodedString));
    } catch {
      return null;
    }
  }

  encodeObject(obj: Object): string {
    return (
      this.generateRandomString(startConcatString) +
      btoa(JSON.stringify(obj)) +
      this.generateRandomString(finalConcatString)
    );
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  convertNumbers(text: string): string {
    const persianNumbers = '۰۱۲۳۴۵۶۷۸۹';
    const arabicNumbers = '٠١٢٣٤٥٦٧٨٩';
    const englishNumbers = '0123456789';
    return text
      .split('')
      .map((c) => {
        const i = persianNumbers.indexOf(c);
        if (i >= 0) return englishNumbers.charAt(i);
        else return c;
      })
      .map((c) => {
        const i = arabicNumbers.indexOf(c);
        if (i >= 0) return englishNumbers.charAt(i);
        else return c;
      })
      .join('');
  }

  isInternalLink(link: string): { isInternal: boolean; url: URL | null } {
    try {
      const calcLink = new URL(link, window.location.origin);
      return {
        isInternal: calcLink.origin === window.location.origin,
        url: calcLink,
      };
    } catch (e) {
      return {
        isInternal: false,
        url: null,
      };
    }
  }
}
